import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Button,
    Space,
    Avatar,
    message,
    Empty,
    Popconfirm,
    Typography,
    Divider,
    Tag,
    theme,
} from 'antd';
import {
    SendOutlined,
    DeleteOutlined,
    CommentOutlined,
} from '@ant-design/icons';
import { ordersApi } from '../../../api/client';
import RichMentionEditor from '../../../components/common/RichMentionEditor';
import MentionDisplay from '../../../components/common/MentionDisplay';
import { useNotificationRefresh } from '../../../hooks/useNotificationRefresh';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pl';

dayjs.extend(relativeTime);
dayjs.locale('pl');

const { Text } = Typography;

interface Comment {
    id: string;
    content: string;
    createdAt: string;
    author: { id: string; firstName: string; lastName: string };
    mentionedUserIds?: string[];
}

interface Props {
    orderId: string;
    isMobile?: boolean;
}

export default function OrderComments({ orderId, isMobile }: Props) {
    const { token } = theme.useToken();
    const queryClient = useQueryClient();
    const refreshNotifications = useNotificationRefresh();

    // Comment form state
    const [commentText, setCommentText] = useState('');

    // Queries
    const { data: comments = [], isLoading } = useQuery<Comment[]>({
        queryKey: ['order-comments', orderId],
        queryFn: () => ordersApi.getComments(orderId).then((r: any) => r.data),
        enabled: !!orderId,
    });

    // Mutations
    const createCommentMutation = useMutation({
        mutationFn: (data: any) => ordersApi.addComment(orderId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['order-comments', orderId] });
            refreshNotifications();
            setCommentText('');
            message.success('Komentarz dodany');
        },
        onError: () => message.error('Błąd dodawania komentarza'),
    });

    const deleteCommentMutation = useMutation({
        mutationFn: (id: string) => ordersApi.deleteComment(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['order-comments', orderId] });
            refreshNotifications();
            message.success('Usunięto');
        },
        onError: () => message.error('Błąd usuwania'),
    });

    // Handlers
    const handleAddComment = () => {
        if (!commentText.trim()) return;
        createCommentMutation.mutate({
            content: commentText,
        });
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            background: token.colorFillQuaternary,
            borderRadius: 8,
            padding: isMobile ? 12 : 16,
            height: '100%',
            minHeight: isMobile ? 400 : 500,
        }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <CommentOutlined style={{ fontSize: 16 }} />
                <Text strong style={{ fontSize: 16 }}>Komentarze</Text>
                <Tag style={{ marginLeft: 'auto' }}>{comments.length}</Tag>
            </div>

            {/* Comment input */}
            <div style={{ marginBottom: 16 }}>
                <RichMentionEditor
                    value={commentText}
                    onChange={setCommentText}
                    placeholder="Napisz komentarz... (@wzmianka)"
                    minRows={2}
                    onPressEnter={handleAddComment}
                />
                <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleAddComment}
                    loading={createCommentMutation.isPending}
                    disabled={!commentText.trim()}
                    style={{ marginTop: 8, width: '100%' }}
                >
                    Wyślij
                </Button>
            </div>

            <Divider style={{ margin: '8px 0 16px 0' }} />

            {/* Comments list */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                paddingRight: 4,
            }}>
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: 20 }}>Ładowanie...</div>
                ) : comments.length === 0 ? (
                    <Empty
                        description={<Text type="secondary">Brak komentarzy</Text>}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        style={{ margin: 'auto' }}
                    />
                ) : (
                    comments.map(comment => (
                        <div
                            key={comment.id}
                            style={{
                                display: 'flex',
                                gap: 12,
                                background: token.colorBgContainer,
                                padding: 12,
                                borderRadius: 8,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            }}
                        >
                            <Avatar
                                style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }}
                                size={isMobile ? 'small' : 'default'}
                            >
                                {comment.author.firstName[0]}
                            </Avatar>

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                    <div>
                                        <Text strong style={{ fontSize: 13 }}>
                                            {comment.author.firstName} {comment.author.lastName}
                                        </Text>
                                        <div style={{ fontSize: 11, color: token.colorTextDescription }}>
                                            {dayjs(comment.createdAt).fromNow()}
                                        </div>
                                    </div>

                                    <Popconfirm
                                        title="Usunąć komentarz?"
                                        onConfirm={() => deleteCommentMutation.mutate(comment.id)}
                                        okText="Tak"
                                        cancelText="Nie"
                                    >
                                        <Button
                                            size="small"
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                                            style={{ padding: '0 4px', height: 24, opacity: 0.6 }}
                                        />
                                    </Popconfirm>
                                </div>

                                <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                                    <MentionDisplay content={comment.content} />
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
